import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { SocketContext } from "../contexts/socket";
import NETWORKS from "../networks";
import GAMES, {
  MODULO_COINFLIP,
  MODULO_ETHEROLL,
  MODULO_ROLLADICE,
  MODULO_ROULETTE,
  MODULO_TWODICE,
} from "../games";
import { getTransactions } from "../models/transactions";
import {
  FormatGui,
  getRange,
  getRouletteColor,
  truncateAddress,
} from "../utils";
import SessionContext from "../contexts/session";
import { useTranslation, Trans } from "react-i18next";

export default function History({ game, address, all = false }) {
  const socket = useContext(SocketContext);
  const { account } = useContext(SessionContext);

  const [onlyMe, setOnlyMe] = useState(false);

  const handleChange = (event, newOnlyMe) => {
    if (newOnlyMe !== null) {
      setOnlyMe(newOnlyMe);
    }
  };

  const [transactions, setTransactions] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { t, i18n } = useTranslation();

  const columns = [
    {
      id: "game",
      label: "Game",
      format: (transaction) => (
        <Typography>
          <Box
            component="img"
            src={GAMES[Number(transaction.modulo)].iconUrl}
            height={24}
            sx={{ verticalAlign: "middle", mr: 0.5 }}
          />
          {t(GAMES[transaction.modulo].name)}
        </Typography>
      ),
    },
    {
      id: "user",
      label: "User",
      format: (transaction, account) => (
        <Stack
          direction="row"
          component={Link}
          to={`/user/${transaction.fromAddress}`}
          spacing={0.5}
          sx={{
            alignItems: "center",
            textDecoration: "none",
            color: "white",
          }}
        >
          <Avatar sx={{ width: 30, height: 30 }} />
          <Typography color="primary">
            {transaction.fromAddress === account
              ? t("YOU")
              : transaction?.user?.username ||
                truncateAddress(transaction.fromAddress)}
          </Typography>
        </Stack>
      ),
    },
    {
      id: "betAmount",
      label: "Bet Amount",
      format: (transaction) => (
        <Typography>
          <FormatGui
            wei={transaction.betValue}
            network={NETWORKS[transaction.chainId]}
          />
        </Typography>
      ),
    },
    {
      id: "inputs",
      label: "Bet",
      format: (transaction) => <FormatInputs transaction={transaction} />,
    },
    {
      id: "result",
      label: "Result",
      format: (transaction) => (
        <FormatInputs transaction={transaction} isResult={true} />
      ),
    },
    {
      id: "payout",
      label: "Payout",
      format: (transaction) => (
        <Typography
          fontWeight="bold"
          color={transaction.isWinner ? "success.main" : ""}
        >
          <FormatGui
            wei={transaction.wonValue || 0}
            network={NETWORKS[transaction.chainId]}
          />
        </Typography>
      ),
    },
    {
      id: "jackpot",
      label: "Jackpot",
      format: (transaction) =>
        transaction.isJackpotWinner ? (
          <FormatGui
            wei={transaction.jackpotValue}
            network={NETWORKS[transaction.chainId]}
          />
        ) : (
          transaction.jackpotRng
        ),
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    let didCancel = false;
    async function updateHistory() {
      if (!didCancel) {
        const addressParam = onlyMe ? account : address;
        let transactions = null;
        if (!all) {
          transactions = await getTransactions(null, addressParam);
        } else {
          transactions = await getTransactions(game?.modulo, addressParam);
        }
        setTransactions(transactions);
      }
    }
    updateHistory();
    return () => {
      didCancel = true;
    };
  }, [game, address, onlyMe, account, all]);

  useEffect(() => {
    if (socket) {
      function on_new_transaction(transaction) {
        if (!all) {
          if (game && Number(transaction.modulo) !== game.modulo) return;
        }
        if (address && transaction.fromAddress !== address) return;
        setTransactions((transactions) => [transaction, ...transactions]);
      }
      function on_new_jackpot(bet) {
        setTransactions((transactions) =>
          transactions.map((transaction) => {
            if (transaction._id === bet.txId) {
              return {
                ...transaction,
                jackpotValue: bet.jackpotValue,
                isJackpotWinner: true,
              };
            }
            return transaction;
          })
        );
      }
      socket.on("new_transaction", on_new_transaction);
      socket.on("new_jackpot", on_new_jackpot);
      return () => {
        socket.off("new_transaction", on_new_transaction);
        socket.off("new_jackpot", on_new_jackpot);
      };
    }
  }, [socket, address, all, game]);

  return (
    <Stack spacing={2}>
      {account && !address ? (
        <ToggleButtonGroup
          color="primary"
          value={onlyMe}
          exclusive
          onChange={handleChange}
          aria-label={t("Platform")}
        >
          <ToggleButton value={true}>
            <Trans t={t}>My bets</Trans>
          </ToggleButton>
          <ToggleButton value={false}>
            <Trans t={t}>All bets</Trans>
          </ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <Typography variant="h6">
          <Trans t={t}>History</Trans>
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{ width: "100%", overflow: "hidden", borderRadius: 2 }}
      >
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {t(column.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                return transactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transaction) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={transaction._id}
                      >
                        {columns.map((column) => {
                          const value = transaction[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format
                                ? column.format(transaction, account)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={transactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("Rows per page")}
        />
      </Paper>
    </Stack>
  );
}

export function FormatInputs({ transaction, isResult }) {  
  const theme = useTheme();
  const modulo = Number(transaction.modulo);
  if (modulo === MODULO_COINFLIP) {
    if (isResult) {
      return (
        <img
          style={{ display: "block" }}
          src={`/img/${
            transaction.isWinner
              ? Number(transaction.betMask) === 1
                ? "headwon"
                : "tailwon"
              : Number(transaction.betMask) === 1
              ? "tailloss"
              : "headloss"
          }.svg`}
          height={24}
          alt=""
        />
      );
    }
    return (
      <img
        src={`/img/${transaction.betMask === "1" ? "head" : "tail"}.svg`}
        style={{ display: "block" }}
        height={24}
        alt=""
      />
    );
  } else if (modulo === MODULO_ROLLADICE) {
    if (isResult) {
      return (
        <SmallDice
          n={Number(transaction.roll)}
          color={
            isResult && transaction.isWinner
              ? theme.palette.success.main
              : "white"
          }
        />
      );
    }
    return (
      <Stack>
        {[
          // [2, 3, 4, 5],
          // [6, 7, 8, 9],
          // [10, 11, 12],
          [1, 2, 3],
          [4, 5, 6],
        ].map((diceRow, diceRowIndex) => (
          <Stack key={diceRowIndex} direction="row">
            {diceRow.map((input, inputIndex) => (
              <div
                key={inputIndex}
                style={{
                  opacity: transaction.inputs
                    .map((i) => Number(i))
                    .includes(input - 1)
                    ? 1
                    : 0.25,
                }}
              >
                <SmallDice
                  n={Number(input) - 1}
                  color={
                    isResult && transaction.isWinner
                      ? theme.palette.success.main
                      : "white"
                  }
                />
              </div>
            ))}
          </Stack>
        ))}
      </Stack>
    );
  } else if (modulo === MODULO_TWODICE) {
    if (isResult) {
      return (
        <div style={{ display: "flex" }}>
          <TwoDice
            n={Number(transaction.roll)}
            color={transaction.isWinner ? theme.palette.success.main : "white"}
          />
        </div>
      );
    }
    return (
      <Stack spacing={0.5}>
        {[
          [2, 3, 4, 5],
          [6, 7, 8, 9],
          [10, 11, 12],
        ].map((diceRow, diceRowIndex) => (
          <Stack key={diceRowIndex} spacing={0.5} direction="row">
            {diceRow.map((input, inputIndex) => (
              <div
                key={inputIndex}
                style={{
                  opacity: transaction.inputs
                    .map((i) => Number(i))
                    .includes(input)
                    ? 1
                    : 0.25,
                }}
              >
                <TwoDice
                  key={inputIndex}
                  n={Number(input)}
                  color={
                    isResult && transaction.isWinner
                      ? theme.palette.success.main
                      : "white"
                  }
                />
              </div>
            ))}
          </Stack>
        ))}
      </Stack>
    );
  } else if (modulo === MODULO_ROULETTE) {
    if (isResult) {
      return <RouletteSquare n={Number(transaction.roll)} isResult={true} />;
    }
    return (
      <Stack direction="row">
        <RouletteSquare
          n={0}
          isSelected={transaction.inputs.map(Number).includes(0)}
        />
        <Stack>
          {[
            getRange(1, 12).map((i) => i * 3),
            getRange(1, 12).map((i) => i * 3 - 1),
            getRange(1, 12).map((i) => i * 3 - 2),
          ].map((inputRow, inputRowIndex) => (
            <Stack key={inputRowIndex} direction="row">
              {inputRow.map((input, inputIndex) => (
                <RouletteSquare
                  key={inputIndex}
                  n={input}
                  isSelected={transaction.inputs.map(Number).includes(input)}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  } else if (modulo === MODULO_ETHEROLL) {
    const inputs = isResult ? [transaction.roll] : transaction.inputs;
    const background =
      isResult && transaction.isWinner
        ? theme.palette.success.dark
        : theme.palette.secondary.dark;
    return <EtherollSquare n={inputs[0]} background={background} />;
  }
}

export function SmallDice({ n, color = "white" }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      {/* <path
        d="M17 2H7C5.67392 2 4.40215 2.52678 3.46447 3.46447C2.52678 4.40215 2 5.67392 2 7V17C2 18.3261 2.52678 19.5979 3.46447 20.5355C4.40215 21.4732 5.67392 22 7 22H17C18.3261 22 19.5979 21.4732 20.5355 20.5355C21.4732 19.5979 22 18.3261 22 17V7C22 5.67392 21.4732 4.40215 20.5355 3.46447C19.5979 2.52678 18.3261 2 17 2ZM20 17C20 17.7956 19.6839 18.5587 19.1213 19.1213C18.5587 19.6839 17.7956 20 17 20H7C6.20435 20 5.44129 19.6839 4.87868 19.1213C4.31607 18.5587 4 17.7956 4 17V7C4 6.20435 4.31607 5.44129 4.87868 4.87868C5.44129 4.31607 6.20435 4 7 4H17C17.7956 4 18.5587 4.31607 19.1213 4.87868C19.6839 5.44129 20 6.20435 20 7V17Z"
        fill="black"
      ></path> */}
      <path
        d="M17 2H7C5.67392 2 4.40215 2.52678 3.46447 3.46447C2.52678 4.40215 2 5.67392 2 7V17C2 18.3261 2.52678 19.5979 3.46447 20.5355C4.40215 21.4732 5.67392 22 7 22H17C18.3261 22 19.5979 21.4732 20.5355 20.5355C21.4732 19.5979 22 18.3261 22 17V7C22 5.67392 21.4732 4.40215 20.5355 3.46447C19.5979 2.52678 18.3261 2 17 2Z"
        fill={color}
      ></path>
      {n === 0 && (
        <>
          <circle cx="12" cy="12" r="2" fill="black"></circle>
        </>
      )}
      {n === 1 && (
        <>
          <circle cx="8" cy="8" r="2" fill="black"></circle>
          <circle cx="16" cy="16" r="2" fill="black"></circle>
        </>
      )}
      {n === 2 && (
        <>
          <circle cx="12" cy="12" r="2" fill="black"></circle>
          <circle cx="8" cy="8" r="2" fill="black"></circle>
          <circle cx="16" cy="16" r="2" fill="black"></circle>
        </>
      )}
      {n === 3 && (
        <>
          <circle cx="8" cy="8" r="2" fill="black"></circle>
          <circle cx="16" cy="8" r="2" fill="black"></circle>
          <circle cx="8" cy="16" r="2" fill="black"></circle>
          <circle cx="16" cy="16" r="2" fill="black"></circle>
        </>
      )}
      {n === 4 && (
        <>
          <circle cx="8" cy="8" r="2" fill="black"></circle>
          <circle cx="16" cy="8" r="2" fill="black"></circle>
          <circle cx="12" cy="12" r="2" fill="black"></circle>
          <circle cx="8" cy="16" r="2" fill="black"></circle>
          <circle cx="16" cy="16" r="2" fill="black"></circle>
        </>
      )}
      {n === 5 && (
        <>
          <circle cx="8" cy="7" r="2" fill="black"></circle>
          <circle cx="8" cy="12" r="2" fill="black"></circle>
          <circle cx="8" cy="17" r="2" fill="black"></circle>
          <circle cx="16" cy="7" r="2" fill="black"></circle>
          <circle cx="16" cy="12" r="2" fill="black"></circle>
          <circle cx="16" cy="17" r="2" fill="black"></circle>
        </>
      )}
    </svg>
  );
}

function TwoDice({ n, color = "white" }) {
  return (
    <div style={{ position: "relative" }}>
      <svg
        width="24"
        height="24"
        viewBox="0 0 388 339"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <g id="#fcfcfcfe">
          <path
            fill={color}
            opacity="1.00"
            d=" M 141.25 13.25 C 153.41 -0.42 175.70 -3.77 190.63 7.38 C 206.67 22.58 221.93 38.81 238.15 53.63 C 250.20 50.16 262.35 47.01 274.38 43.46 C 286.93 39.50 301.40 34.60 314.00 41.02 C 326.30 45.74 332.97 58.06 335.68 70.33 C 352.94 130.07 370.88 189.62 388.00 249.41 L 388.00 256.60 C 385.85 268.09 379.19 279.45 368.03 284.08 C 345.97 291.77 323.05 296.90 300.67 303.65 C 262.11 314.44 223.51 325.04 184.99 335.97 C 176.49 338.82 166.96 338.94 158.65 335.38 C 146.21 330.22 139.75 317.59 129.76 309.24 C 96.19 276.49 62.74 243.61 29.77 210.23 C 19.35 199.99 5.66 191.13 1.74 176.19 C -0.67 165.19 1.21 152.63 9.28 144.28 C 52.72 100.02 97.78 57.47 141.25 13.25 Z"
          />
        </g>
      </svg>
      <div
        style={{
          color: "black",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {n}
      </div>
    </div>
  );
}

function RouletteSquare({ n, isSelected = true, isResult = false }) {
  const color = getRouletteColor(Number(n));
  return (
    <Box
      sx={{
        borderRadius: 0.25,
        background: color,
        width: 20,
        // height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: isSelected && !isResult ? "1px solid #40ff00" : "",
        opacity: isSelected ? 100 : 0.25,
      }}
    >
      {n}
    </Box>
  );
}

function EtherollSquare({ n, background }) {
  return (
    <Box
      sx={{
        borderRadius: 0.25,
        background,
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {n}
    </Box>
  );
}
